// src/pages/settings/page.tsx
import { PageHeader } from '@/components/layout/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ThemeSelector } from './components/theme-selector';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useState, useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useUser } from '@clerk/clerk-react';
import api from '@/lib/api/axios';
import { Upload, User as UserIcon } from 'lucide-react';

interface SettingsState {
  emailNotifications: boolean;
  marketingEmails: boolean;
  activityDigest: boolean;
}

interface ProfileData {
  photoUrl: string | null;
  firstName: string | null;
  lastName: string | null;
}

export default function SettingsPage() {
  const { user } = useUser();
  const [settings, setSettings] = useState<SettingsState>({
    emailNotifications: true,
    marketingEmails: false,
    activityDigest: true,
  });
  const [profile, setProfile] = useState<ProfileData>({
    photoUrl: null,
    firstName: null,
    lastName: null,
  });
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Load profile data
    const loadProfile = async () => {
      try {
        const response = await api.get('/profile');
        setProfile({
          photoUrl: response.data.photoUrl || null,
          firstName: response.data.firstName || null,
          lastName: response.data.lastName || null,
        });
      } catch (error) {
      }
    };
    loadProfile();
  }, []);

  const updateSetting = (key: keyof SettingsState) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: !prev[key] };
      toast.success('Settings updated successfully');
      return newSettings;
    });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setIsUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await api.post('/profile/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setProfile(prev => ({ ...prev, photoUrl: response.data.photoUrl }));
      toast.success('Profile photo updated successfully');
    } catch (error) {
      toast.error('Failed to upload profile photo');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      await api.put('/profile', {
        firstName: profile.firstName,
        lastName: profile.lastName,
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setIsSavingProfile(false);
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader title="Settings" />

      <div className="grid gap-6">
        {/* Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>
              Manage your profile information and photo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Profile Photo */}
            <div className="space-y-4">
              <Label>Profile Photo</Label>
              <div className="flex items-center gap-6">
                <div className="relative w-24 h-24 bg-muted flex items-center justify-center overflow-hidden">
                  {profile.photoUrl ? (
                    <img
                      src={profile.photoUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserIcon className="w-12 h-12 text-muted-foreground" />
                  )}
                </div>
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={isUploadingPhoto}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={isUploadingPhoto}
                  />
                  <p className="text-xs text-muted-foreground">
                    Recommended: Square image, max 5MB
                  </p>
                </div>
              </div>
            </div>

            {/* Name Fields */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={profile.firstName || ''}
                  onChange={(e) =>
                    setProfile((prev) => ({ ...prev, firstName: e.target.value }))
                  }
                  placeholder="Enter first name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={profile.lastName || ''}
                  onChange={(e) =>
                    setProfile((prev) => ({ ...prev, lastName: e.target.value }))
                  }
                  placeholder="Enter last name"
                />
              </div>
            </div>

            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                value={user?.primaryEmailAddress?.emailAddress || ''}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Email is managed through your Clerk account. Contact Wekesa for an update: 0792589417
              </p>
            </div>

            {/* Save Button */}
            <Button onClick={handleSaveProfile} disabled={isSavingProfile}>
              {isSavingProfile ? 'Saving...' : 'Save Profile'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>
              Customize the appearance of the application
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-between items-center">
            <div>
              <h3 className="text-sm font-medium">Theme</h3>
              <p className="text-sm text-muted-foreground">
                Select your preferred theme
              </p>
            </div>
            <ThemeSelector />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Notifications (Coming Soon)</CardTitle>
            <CardDescription>
              Manage your notification preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-sm font-medium">Email Notifications</h3>
                <p className="text-sm text-muted-foreground">
                  Receive notifications about your account activity
                </p>
              </div>
              <Switch
                checked={settings.emailNotifications}
                onCheckedChange={() => updateSetting('emailNotifications')}
              />
            </div>
            {/* <div className="flex justify-between items-center">
              <div>
                <h3 className="text-sm font-medium">Marketing Emails</h3>
                <p className="text-sm text-muted-foreground">
                  Receive emails about new features and updates
                </p>
              </div>
              <Switch
                checked={settings.marketingEmails}
                onCheckedChange={() => updateSetting('marketingEmails')}
              />
            </div> */}
            {/* <div className="flex justify-between items-center">
              <div>
                <h3 className="text-sm font-medium">Activity Digest</h3>
                <p className="text-sm text-muted-foreground">
                  Receive a weekly digest of your activity
                </p>
              </div>
              <Switch
                checked={settings.activityDigest}
                onCheckedChange={() => updateSetting('activityDigest')}
              />
            </div> */}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}